import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";
import { api } from "../api";
import { buildItemUrl } from "../utils/itemUrl";
import { toDisplayImageUrl } from "../utils/imageUrl";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";

const PAGE_SIZE = 50;
const MAX_POOL_ITEMS = 150;
const REFRESH_INTERVAL_MS = 90_000;
const MAX_TICKER_ITEMS = 40;
const FIRST_VISIBLE_IMAGES_DESKTOP = 6;
const FIRST_VISIBLE_IMAGES_MOBILE = 3;
const ENABLE_TICKER_IMAGES_ON_MOBILE = true;
const TICKER_PIXELS_PER_SECOND = 105;

const tickerScroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const fallbackItem = {
  item_id: "placeholder",
  name: "Market tape unavailable",
  image_url: null,
  price: null,
  change_pct: null,
  direction: "flat",
};

const createSessionSeed = () => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const parts = new Uint32Array(3);
    window.crypto.getRandomValues(parts);
    return Array.from(parts, (value) => value.toString(36)).join("-");
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

const dedupeAndCapItems = (items) => {
  const seenIds = new Set();
  const seenNames = new Set();
  const unique = [];

  for (const item of items) {
    if (!item || item.item_id == null) continue;
    const normalizedId = String(item.item_id);
    const normalizedName = String(item.name || "").trim().toLowerCase().replace(/\s+/g, " ");
    if (seenIds.has(normalizedId) || (normalizedName && seenNames.has(normalizedName))) continue;
    seenIds.add(normalizedId);
    if (normalizedName) seenNames.add(normalizedName);
    unique.push(item);
  }

  return unique.length > MAX_POOL_ITEMS ? unique.slice(-MAX_POOL_ITEMS) : unique;
};

const rotateItems = (items, startIndex) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const offset = Number(startIndex) || 0;
  const normalizedOffset = ((offset % items.length) + items.length) % items.length;
  if (normalizedOffset === 0) return items;
  return [...items.slice(normalizedOffset), ...items.slice(0, normalizedOffset)];
};

const hashSeedToIndex = (seed, length) => {
  if (!length || length <= 0) return 0;
  const value = String(seed || "");
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash % length;
};

const tickerItemsMatch = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((item, index) => String(item?.item_id) === String(right[index]?.item_id));
};

const buildTickerBatch = (items, seed) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  return rotateItems(items, hashSeedToIndex(seed, items.length)).slice(0, MAX_TICKER_ITEMS);
};

const toTickerImageUrl = (value) => {
  const displayUrl = toDisplayImageUrl(value);
  if (!displayUrl || !displayUrl.includes("/api/image-proxy?")) return displayUrl;
  return `${displayUrl}&size=ticker`;
};

const TickerBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const navigate = useNavigate();
  const { showPrices, pricesLoaded } = usePriceVisibility();

  const [activeItems, setActiveItems] = useState([]);
  const [animationDuration, setAnimationDuration] = useState(null);
  const [animationEnabled, setAnimationEnabled] = useState(false);

  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const visibleOffsetRef = useRef(null);
  const hasMoreRef = useRef(true);
  const didBootstrapRef = useRef(false);
  const seedRef = useRef(createSessionSeed());
  const fullPoolRef = useRef([]);
  const activeItemsRef = useRef([]);
  const pendingItemsRef = useRef([]);
  const hasPendingUpdateRef = useRef(false);
  const mountedRef = useRef(true);
  const isMobileRef = useRef(isMobile);
  const reducedMotionRef = useRef(prefersReducedMotion);
  const tapeARef = useRef(null);
  const activeTapeWidthRef = useRef(0);
  const pendingDurationRef = useRef(null);
  const isCommittingRefreshRef = useRef(false);
  const boundaryFrameRef = useRef(null);
  const startupFrameRef = useRef(null);
  const startupSecondFrameRef = useRef(null);
  const hasStartedAnimationRef = useRef(false);
  const animationDurationRef = useRef(null);

  animationDurationRef.current = animationDuration;

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    reducedMotionRef.current = prefersReducedMotion;
    if (prefersReducedMotion && hasPendingUpdateRef.current && pendingItemsRef.current.length > 0) {
      const nextItems = pendingItemsRef.current;
      pendingItemsRef.current = [];
      hasPendingUpdateRef.current = false;
      activeItemsRef.current = nextItems;
      setActiveItems(nextItems);
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (boundaryFrameRef.current != null) {
        window.cancelAnimationFrame(boundaryFrameRef.current);
      }
      if (startupFrameRef.current != null) {
        window.cancelAnimationFrame(startupFrameRef.current);
      }
      if (startupSecondFrameRef.current != null) {
        window.cancelAnimationFrame(startupSecondFrameRef.current);
      }
    };
  }, []);

  const fmtUSD = (n) => {
    if (n == null) return "--";
    if (!Number.isFinite(n)) return "--";
    return n.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });
  };

  const fmtDeltaUSDFromCents = (deltaCents) => {
    if (deltaCents == null || !Number.isFinite(deltaCents)) return "";
    const dollars = Math.abs(deltaCents) / 100;
    const text = dollars.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });
    return deltaCents >= 0 ? `+${text}` : `-${text}`;
  };

  const fmtPct = (pct) => {
    if (pct == null || !Number.isFinite(pct)) return "";
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct.toFixed(1)}%`;
  };

  const preloadLeadingImages = useCallback((items) => {
    const count = isMobileRef.current
      ? FIRST_VISIBLE_IMAGES_MOBILE
      : FIRST_VISIBLE_IMAGES_DESKTOP;
    for (const item of (items || []).slice(0, count)) {
      const imageUrl = toTickerImageUrl(item?.image_url);
      if (!imageUrl) continue;
      const image = new Image();
      image.decoding = "async";
      image.src = imageUrl;
    }
  }, []);

  const queueTickerBatch = useCallback((nextItems) => {
    if (!mountedRef.current || !Array.isArray(nextItems) || nextItems.length === 0) return false;
    if (tickerItemsMatch(nextItems, activeItemsRef.current)) {
      pendingItemsRef.current = [];
      hasPendingUpdateRef.current = false;
      return false;
    }
    if (activeItemsRef.current.length === 0 || reducedMotionRef.current) {
      activeItemsRef.current = nextItems;
      pendingItemsRef.current = [];
      hasPendingUpdateRef.current = false;
      setActiveItems(nextItems);
      return true;
    }
    if (tickerItemsMatch(nextItems, pendingItemsRef.current)) return false;
    preloadLeadingImages(nextItems);
    pendingItemsRef.current = nextItems;
    hasPendingUpdateRef.current = true;
    return true;
  }, [preloadLeadingImages]);

  const mergeIncomingItems = useCallback((incoming) => {
    const merged = dedupeAndCapItems([...fullPoolRef.current, ...incoming]);
    if (visibleOffsetRef.current == null && merged.length > 0) {
      visibleOffsetRef.current = hashSeedToIndex(seedRef.current, merged.length);
    }
    fullPoolRef.current = merged;
    const offset = visibleOffsetRef.current ?? hashSeedToIndex(seedRef.current, merged.length);
    queueTickerBatch(rotateItems(merged, offset).slice(0, MAX_TICKER_ITEMS));
  }, [queueTickerBatch]);

  const fetchNext = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return false;

    loadingRef.current = true;
    const requestOffset = offsetRef.current;

    try {
      const { data } = await api.get("/api/ticker", {
        params: {
          limit: PAGE_SIZE,
          offset: requestOffset,
          seed: seedRef.current,
        },
      });

      const incoming = Array.isArray(data?.items) ? data.items : [];
      if (!mountedRef.current) return false;
      mergeIncomingItems(incoming);

      const nextOffset = Number(data?.nextOffset);
      const resolvedNextOffset =
        Number.isFinite(nextOffset) && nextOffset >= 0
          ? nextOffset
          : requestOffset + incoming.length;
      const canAdvance = resolvedNextOffset > requestOffset || incoming.length > 0;

      offsetRef.current = resolvedNextOffset;
      hasMoreRef.current = Boolean(data?.hasMore) && canAdvance;
      return incoming.length > 0;
    } catch (_e) {
      hasMoreRef.current = false;
      return false;
    } finally {
      loadingRef.current = false;
    }
  }, [mergeIncomingItems]);

  const refreshTicker = useCallback(async () => {
    if (loadingRef.current) return false;

    loadingRef.current = true;
    try {
      const nextSeed = createSessionSeed();
      const { data } = await api.get("/api/ticker", {
        params: {
          limit: PAGE_SIZE,
          offset: 0,
          seed: nextSeed,
          fresh: 1,
        },
      });

      const incoming = Array.isArray(data?.items) ? data.items : [];
      if (!mountedRef.current) return false;
      const refreshedPool = dedupeAndCapItems(incoming);
      if (refreshedPool.length === 0) return false;
      seedRef.current = nextSeed;
      visibleOffsetRef.current = incoming.length > 0
        ? hashSeedToIndex(nextSeed, incoming.length)
        : null;
      offsetRef.current = Number.isFinite(Number(data?.nextOffset))
        ? Number(data.nextOffset)
        : incoming.length;
      hasMoreRef.current = Boolean(data?.hasMore);
      fullPoolRef.current = refreshedPool;
      queueTickerBatch(buildTickerBatch(refreshedPool, nextSeed));
      return incoming.length > 0;
    } catch (_e) {
      return false;
    } finally {
      loadingRef.current = false;
    }
  }, [queueTickerBatch]);

  useEffect(() => {
    if (didBootstrapRef.current) return undefined;
    didBootstrapRef.current = true;

    let cancelled = false;

    const bootstrap = async () => {
      const firstPageLoaded = await fetchNext();
      if (cancelled || !firstPageLoaded || !hasMoreRef.current) return;
      await fetchNext();
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [fetchNext]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      refreshTicker();
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [refreshTicker]);

  const displayItems = useMemo(
    () => activeItems.length > 0 ? activeItems : [fallbackItem],
    [activeItems]
  );

  useLayoutEffect(() => {
    if (activeItems.length === 0) return;
    const tapeWidth = tapeARef.current?.getBoundingClientRect().width;
    if (!Number.isFinite(tapeWidth) || tapeWidth <= 0) return;
    const nextDuration = tapeWidth / TICKER_PIXELS_PER_SECOND;
    if (!Number.isFinite(nextDuration) || nextDuration <= 0) return;
    const durationChanged = !Number.isFinite(animationDuration)
      || Math.abs(animationDuration - nextDuration) >= 0.05;

    if (prefersReducedMotion) {
      activeTapeWidthRef.current = tapeWidth;
      pendingDurationRef.current = null;
      if (durationChanged) setAnimationDuration(nextDuration);
      return;
    }

    if (!hasStartedAnimationRef.current) {
      activeTapeWidthRef.current = tapeWidth;
      pendingDurationRef.current = null;
      setAnimationEnabled(false);
      if (durationChanged) setAnimationDuration(nextDuration);
      if (startupFrameRef.current != null) {
        window.cancelAnimationFrame(startupFrameRef.current);
      }
      if (startupSecondFrameRef.current != null) {
        window.cancelAnimationFrame(startupSecondFrameRef.current);
      }
      startupFrameRef.current = window.requestAnimationFrame(() => {
        startupFrameRef.current = null;
        if (!mountedRef.current) return;
        startupSecondFrameRef.current = window.requestAnimationFrame(() => {
          startupSecondFrameRef.current = null;
          const committedDuration = animationDurationRef.current;
          if (!mountedRef.current || !Number.isFinite(committedDuration) || committedDuration <= 0) return;
          hasStartedAnimationRef.current = true;
          setAnimationEnabled(true);
        });
      });
      return;
    }

    if (isCommittingRefreshRef.current) {
      activeTapeWidthRef.current = tapeWidth;
      pendingDurationRef.current = null;
      if (durationChanged) setAnimationDuration(nextDuration);
      if (boundaryFrameRef.current != null) {
        window.cancelAnimationFrame(boundaryFrameRef.current);
      }
      boundaryFrameRef.current = window.requestAnimationFrame(() => {
        boundaryFrameRef.current = null;
        if (!mountedRef.current) return;
        isCommittingRefreshRef.current = false;
        setAnimationEnabled(true);
      });
      return;
    }

    if (Math.abs(activeTapeWidthRef.current - tapeWidth) >= 1 && durationChanged) {
      pendingDurationRef.current = nextDuration;
    }
  }, [activeItems, animationDuration, isMobile, prefersReducedMotion, pricesLoaded, showPrices]);

  useEffect(() => {
    if (!displayItems.length) return undefined;

    const preloaded = [];
    const firstVisibleImageCount = isMobile
      ? FIRST_VISIBLE_IMAGES_MOBILE
      : FIRST_VISIBLE_IMAGES_DESKTOP;
    for (const item of displayItems.slice(0, firstVisibleImageCount)) {
      const imageUrl = toTickerImageUrl(item?.image_url);
      if (!imageUrl) continue;
      const image = new Image();
      image.decoding = "async";
      image.src = imageUrl;
      preloaded.push(image);
    }

    return () => {
      preloaded.length = 0;
    };
  }, [displayItems, isMobile]);

  const handleAnimationIteration = useCallback(() => {
    const hasPendingItems = hasPendingUpdateRef.current && pendingItemsRef.current.length > 0;
    if (!hasPendingItems && pendingDurationRef.current == null) return;

    isCommittingRefreshRef.current = true;
    setAnimationEnabled(false);

    if (hasPendingItems) {
      const nextItems = pendingItemsRef.current;
      pendingItemsRef.current = [];
      hasPendingUpdateRef.current = false;
      activeItemsRef.current = nextItems;
      setActiveItems(nextItems);
      return;
    }

    const nextDuration = pendingDurationRef.current;
    pendingDurationRef.current = null;
    if (Number.isFinite(nextDuration)) {
      setAnimationDuration((current) => Math.abs(current - nextDuration) < 0.05 ? current : nextDuration);
    }
  }, []);

  const onItemClick = (item) => {
    const itemId = item?.item_id ?? item?.id;
    if (!Number.isFinite(Number(itemId))) return;
    navigate(buildItemUrl(item));
  };

  const renderEntry = (item, idx, duplicateTape = false) => {
    const price = Number(item?.price);
    const priceText = showPrices && pricesLoaded ? fmtUSD(Number.isFinite(price) ? price : null) : null;
    const pct = item?.change_pct == null ? null : Number(item.change_pct);
    const deltaCents = item?.delta_cents == null ? null : Number(item.delta_cents);
    const hasValidDelta =
      deltaCents != null &&
      Number.isFinite(deltaCents) &&
      deltaCents !== 0;

    const hasChange = showPrices && pricesLoaded && hasValidDelta;
    const direction = hasValidDelta ? (deltaCents > 0 ? "up" : "down") : "flat";

    const deltaText = hasValidDelta ? fmtDeltaUSDFromCents(deltaCents) : "";
    const pctText =
      hasValidDelta && pct != null && Number.isFinite(pct)
        ? fmtPct(pct)
        : "";

    let changeText = "";
    if (hasValidDelta && pctText) {
      changeText = `${deltaText} (${pctText})`;
    } else if (hasValidDelta) {
      changeText = deltaText;
    }

    const arrow = direction === "up" ? "\u2191" : direction === "down" ? "\u2193" : "";
    const color =
      direction === "up"
        ? "success.main"
        : direction === "down"
        ? "error.main"
        : "rgba(230,237,247,0.68)";
    const label = (item?.name || `Item #${item?.item_id || ""}`).trim();
    const isClickable = Number.isFinite(Number(item?.item_id));
    const shouldLoadImage = ENABLE_TICKER_IMAGES_ON_MOBILE || !isMobile;
    const firstVisibleImageCount = isMobile
      ? FIRST_VISIBLE_IMAGES_MOBILE
      : FIRST_VISIBLE_IMAGES_DESKTOP;
    const imageLoading = !duplicateTape && idx < firstVisibleImageCount ? "eager" : "lazy";
    const entryWidth = hasChange
      ? { xs: 260, sm: 320 }
      : { xs: 205, sm: 250 };
    const labelMaxWidth = hasChange
      ? { xs: 105, sm: 145 }
      : { xs: 95, sm: 125 };

    return (
      <Box
        key={`${item.item_id}-${idx}`}
        component="button"
        type="button"
        onClick={() => onItemClick(item)}
        disabled={!isClickable}
        sx={{
          height: isMobile ? 46 : 50,
          px: 1.25,
          mr: 1.5,
          borderRadius: 1.5,
          border: "none",
          bgcolor: "rgba(255,255,255,0.03)",
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          cursor: isClickable ? "pointer" : "default",
          color: "inherit",
          flexShrink: 0,
          width: entryWidth,
          minWidth: entryWidth,
          whiteSpace: "nowrap",
          transition: "background-color 180ms ease",
          "&:hover": isClickable
            ? {
                bgcolor: "rgba(255,255,255,0.06)",
              }
            : {},
          "&:disabled": { opacity: 1 },
        }}
      >
        <Avatar
          src={shouldLoadImage ? toTickerImageUrl(item?.image_url) || undefined : undefined}
          variant="rounded"
          imgProps={{
            loading: imageLoading,
            decoding: "async",
            style: { width: "100%", height: "100%", objectFit: "cover" },
            onError: (event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/logos/default-item.jpg";
            },
          }}
          sx={{
            width: isMobile ? 32 : 36,
            height: isMobile ? 32 : 36,
            flexShrink: 0,
            fontSize: 13,
            bgcolor: "secondary.main",
          }}
        >
          {(label[0] || "?").toUpperCase()}
        </Avatar>

        <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              minWidth: 0,
              color: "rgba(230,237,247,0.94)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: labelMaxWidth,
              textDecoration: "none",
              ".ticker-item:hover &": { textDecoration: "underline" },
            }}
          >
            {label}
          </Typography>
          {priceText ? <Typography variant="body2" sx={{ color: "rgba(230,237,247,0.72)", fontVariantNumeric: "tabular-nums" }}>
            {priceText}
          </Typography> : null}
          {hasChange ? (
            <Typography variant="body2" sx={{ color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              {arrow ? `${arrow} ` : ""}{changeText}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderTape = (keyPrefix, duplicateTape = false, tapeRef = null) => (
    <Box
      ref={tapeRef}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        gap: 1.5,
        px: 3,
        width: "max-content",
      }}
    >
      {displayItems.map((item, idx) => (
        <Box
          className="ticker-item"
          key={`${keyPrefix}-${item.item_id}-${idx}`}
          sx={{ display: "inline-flex", flexShrink: 0 }}
        >
          {renderEntry(item, idx, duplicateTape)}
        </Box>
      ))}
    </Box>
  );

  const canAnimate =
    !prefersReducedMotion &&
    animationEnabled &&
    Number.isFinite(animationDuration) &&
    animationDuration > 0;

  return (
    <Box
      sx={{
        height: isMobile ? 56 : 60,
        bgcolor: "#0f1a2e",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        "&:hover .ticker-track": { animationPlayState: "paused" },
      }}
    >
      <Box
        className="ticker-track"
        sx={{
          display: "inline-flex",
          width: "max-content",
          flexShrink: 0,
          minWidth: "max-content",
          whiteSpace: "nowrap",
          alignItems: "center",
          willChange: "transform",
          backfaceVisibility: "hidden",
          transform: canAnimate ? "translateZ(0)" : "translateX(0) translateZ(0)",
          animation: canAnimate ? `${tickerScroll} ${animationDuration}s linear infinite` : "none",
          pl: 0,
          pr: 0,
        }}
        onAnimationIteration={handleAnimationIteration}
      >
        {renderTape("a", false, tapeARef)}
        {renderTape("b", true)}
      </Box>
    </Box>
  );
};

export default TickerBar;
